import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { AutomationRulesService } from '@app/features/automation-rules/automation-rules.service';
import { Rule } from 'src/app/sharedModule/models/rule.model';
import { AutomationRulesActions } from '../actions';
import { AutomationRuleCreateDialog } from '../components/dialog/create/automation-rule-create-dialog';
import { AutomationRuleEditDialog } from '../components/dialog/edit/automation-rule-edit-dialog';
import { AutomationRulesListDialog } from '../components/dialog/list/automation-rules-list-dialog';
import { TemplateData } from '../reducers/automation-rules.reducer';
import { Step } from '@app/features/automation-rules/utils';

@Injectable()
export class AutomationRulesEffects {
  currentStep: Step = Step.verify;
  stepToRuleTypeMap = {
    verify: 'order-created',
    assign: 'order-assigned',
    fulfill: 'order-fulfilled',
    track: 'order-tracked',
  };
  ruleCreateDialogRef: MatDialogRef<AutomationRuleCreateDialog, any>;
  ruleEditDialogRef: MatDialogRef<AutomationRuleEditDialog, any>;
  rulesListDialogRef: MatDialogRef<AutomationRulesListDialog, any>;

  constructor(
    private actions$: Actions,
    public dialog: MatDialog,
    private automationRulesService: AutomationRulesService,
  ) {}

  openAutomationRulesListDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AutomationRulesActions.openAutomationRulesListDialog,
        AutomationRulesActions.addRuleSuccess,
        AutomationRulesActions.editRuleSuccess,
      ),
      switchMap((event) => {
        const step = event['step'] || this.currentStep;

        if (step) {
          this.currentStep = step;
        }

        this.rulesListDialogRef = this.dialog.open(AutomationRulesListDialog, {
          panelClass: 'rules-list-dialog',
          data: {
            step,
          },
        });
        return of(AutomationRulesActions.loadTemplateData());
      }),
    ),
  );

  closeAutomationRulesListDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AutomationRulesActions.closeAutomationRulesListDialog),
        map(() => {
          this.rulesListDialogRef.close();
        }),
      ),
    { dispatch: false },
  );

  goBackToAutomationRulesListDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AutomationRulesActions.goBackToAutomationRulesListDialog),
        map((event) => {
          const step = event['step'] || this.currentStep;

          if (step) {
            this.currentStep = step;
          }

          this.rulesListDialogRef = this.dialog.open(AutomationRulesListDialog, {
            panelClass: 'rules-list-dialog',
            data: {
              step,
            },
          });
        }),
      ),
    { dispatch: false },
  );

  openAddWorkflowDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.openAddWorkflowDialog),
      switchMap((event) => {
        const step = (event['step'] || this.currentStep) as Step;

        if (step) {
          this.currentStep = step;
        }

        this.ruleCreateDialogRef = this.dialog.open(AutomationRuleCreateDialog, {
          panelClass: 'rules-create-dialog',
          data: {
            step,
          },
          autoFocus: false,
        });
        return [AutomationRulesActions.closeAutomationRulesListDialog(), AutomationRulesActions.loadTemplateData()];
      }),
    ),
  );

  openEditWorkflowDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.openEditWorkflowDialog),
      switchMap((event) => {
        const step = (event['step'] || this.currentStep) as Step;
        const ruleId = event.id;
        const selectedRuleParams = event.params;
        const internalName = event.internalName;
        if (step) {
          this.currentStep = step;
        }

        this.ruleEditDialogRef = this.dialog.open(AutomationRuleEditDialog, {
          panelClass: 'rules-edit-dialog',
          data: {
            step,
            ruleId,
            selectedRuleParams,
            internalName,
          },
          autoFocus: false,
        });
        return [AutomationRulesActions.closeAutomationRulesListDialog()];
      }),
    ),
  );

  loadRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.loadRules),
      switchMap(() => {
        return this.automationRulesService
          .getRulesListData(this.stepToRuleTypeMap[this.currentStep.toLowerCase()])
          .pipe(
            map((rules: Rule[]) => {
              return AutomationRulesActions.loadRulesSuccess({ rules });
            }),
            catchError((error) => {
              console.log('loadRules effect failure');
              return of(
                AutomationRulesActions.loadRulesFailure({
                  errorMessage: error.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  loadTemplateData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.loadTemplateData),
      switchMap(() => {
        return this.automationRulesService.getTemplateData(this.stepToRuleTypeMap[this.currentStep.toLowerCase()]).pipe(
          switchMap((templateData: TemplateData) => {
            return [
              AutomationRulesActions.loadTemplateDataSuccess({
                templateData,
              }),
              AutomationRulesActions.loadRules(),
            ];
          }),
          catchError((error) => {
            console.log('loadTemplateData effect failure');
            return of(
              AutomationRulesActions.loadTemplateDataFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  addRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.addRule),
      switchMap(({ selectedAction, selectedConditions, internalName }) => {
        const ruleData = {
          params: {
            orWhen: selectedConditions.map((conditionGroup) => {
              return {
                andWhen: conditionGroup.map((selectedCondition) => {
                  return { [selectedCondition.name]: selectedCondition.value || selectedCondition.values };
                }),
              };
            }),
            andThen: [
              {
                [selectedAction.name]: selectedAction.value || selectedAction.values,
              },
            ],
          },
          internalName: internalName,
        };
        return this.automationRulesService
          .addRule(this.stepToRuleTypeMap[this.currentStep.toLowerCase()], ruleData)
          .pipe(
            map((rule: Rule) => {
              this.ruleCreateDialogRef.close();
              return AutomationRulesActions.addRuleSuccess({
                rule,
              });
            }),
            catchError((error) => {
              console.log('addRule effect failure');
              return of(
                AutomationRulesActions.addRuleFailure({
                  errorMessage: error.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  editRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.editRule),
      switchMap(({ ruleId, selectedAction, selectedConditions, internalName }) => {
        const ruleData = {
          params: {
            orWhen: selectedConditions.map((conditionGroup) => {
              return {
                andWhen: conditionGroup.map((selectedCondition) => {
                  return { [selectedCondition.name]: selectedCondition.value || selectedCondition.values };
                }),
              };
            }),
            andThen: [
              {
                [selectedAction.name]: selectedAction.value || selectedAction.values,
              },
            ],
          },
          internalName: internalName,
        };
        return this.automationRulesService.updateRule(ruleId, ruleData).pipe(
          map((rule: Rule) => {
            this.ruleEditDialogRef.close();
            return AutomationRulesActions.editRuleSuccess({
              rule,
            });
          }),
          catchError((error) => {
            console.log('editRule effect failure');
            return of(
              AutomationRulesActions.editRuleFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  changeRulePriority$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.changeRulePriority),
      mergeMap(({ id, priority }) => {
        return this.automationRulesService.updateRule(id, { priority }).pipe(
          switchMap((rule: Rule) => {
            return [
              AutomationRulesActions.changeRulePrioritySuccess({
                rule,
              }),
            ];
          }),
          catchError((error) => {
            console.log('changeRulePriority effect failure');
            return of(
              AutomationRulesActions.changeRulePriorityFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  ruleToggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.ruleToggle),
      mergeMap(({ id, enabled }) => {
        return this.automationRulesService.updateRule(id, { enabled }).pipe(
          switchMap((rule: Rule) => {
            return [
              AutomationRulesActions.ruleToggleSuccess({
                rule,
              }),
            ];
          }),
          catchError((error) => {
            console.log('ruleToggle effect failure');
            return of(
              AutomationRulesActions.ruleToggleFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  ruleDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.ruleDelete),
      mergeMap(({ id }) => {
        return this.automationRulesService.deleteRule(id).pipe(
          switchMap((rule: Rule) => {
            return [
              AutomationRulesActions.ruleDeleteSuccess({
                rule,
              }),
              AutomationRulesActions.loadRules(),
            ];
          }),
          catchError((error) => {
            console.log('ruleDelete effect failure');
            return of(
              AutomationRulesActions.ruleDeleteFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  ruleDuplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationRulesActions.ruleDuplicate),
      mergeMap(({ id }) => {
        return this.automationRulesService.duplicateRule(id).pipe(
          switchMap((rule: Rule) => {
            return [
              AutomationRulesActions.ruleDuplicateSuccess({
                rule,
              }),
              AutomationRulesActions.loadRules(),
            ];
          }),
          catchError((error) => {
            console.log('ruleDuplicate effect failure');
            return of(
              AutomationRulesActions.ruleDuplicateFailure({
                errorMessage: error.error,
              }),
            );
          }),
        );
      }),
    ),
  );
}
